import React, { useEffect } from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import useAnalytics from 'helpers/useAnalytics';
import { useDispatch, useSelector } from 'react-redux';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';
import { Prompt, useHistory } from 'react-router';
import { useStatus, useData } from 'resift';
import * as Yup from 'yup';
import delay from 'delay';
import getHostName from 'helpers/getHostName';
import * as Types from './typings';
import { defaultSupportFormValue } from './constants';
import { updateClient } from 'store/client';
import useFormSnackbar from 'components/Form/helpers/useFormSnackbar';
import fixImageUri from 'store/themes/actions/_helpers/fixImageUri';
import useEntityTypes, { PERSON_ENTITY_TYPE_KEY } from 'helpers/useEntityTypes';
import makeEntityTypesFetch from 'store/configuration/actions/entityTypesFetch';
import makeUpdateLayoutFetch from 'pages/ProfileLayout/fetches/updateLayoutFetch';
import verifyNewSubdomain from 'fetches/verifyNewSubdomain';
import { fetchThemes, getThemesFetch } from 'store/themes/actions/getThemes';
import { saveTheme } from 'store/themes/actions/saveTheme';
import isValidColor from '@sift/skipper/styles/isValidColor';
import getOnboardingTasks from 'fetches/getOnboardingTasks';

import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import { Formik, Form } from 'formik';
import Page from 'components/Page/newComponents';
import Breadcrumbs from 'components/Page/Breadcrumbs';
import PageTitle from 'components/Page/PageTitle';
import PageFormToolbar from 'components/Page/newComponents/PageFormToolbar';
import PageFormErrorMessage from 'components/Page/newComponents/PageFormErrorMessage';
import SaveButton from 'components/Page/SaveButton';
import CancelButton from 'components/Page/CancelButton';
import Loader from 'components/Loader';
import ThemingPage from './tabs/Theming/Theming';
import SettingsPage from './tabs/Settings';
import HandleRedirect from 'molecules/HandleRedirect';

function General() {
  const classes = useStyles();
  const analytics = useAnalytics();
  const dispatch = useDispatch();
  const subdomainValidation = useData(verifyNewSubdomain);
  const _client = useSelector((state: any) => state.client);
  const entityTypes = useEntityTypes();
  const entityTypesFetch = makeEntityTypesFetch();
  const userConfig = _get(entityTypes, PERSON_ENTITY_TYPE_KEY);

  useEffect(() => {
    dispatch(fetchThemes());
  }, [fetchThemes]);

  const _themes = useSelector((state: any) => state.themes);
  const themesFetch = getThemesFetch(_themes);
  const themes = _get(themesFetch, 'data.results', []);
  const activeTheme = themes.find((theme: any) => theme.isActive);

  const initialState = {
    client: {
      ..._client,
      subdomain: _get(_client, 'subdomain', ''),
      displayName: _client.displayName || 'Sift App',
      settings: {
        ..._client.settings,
        login: {
          blockCreatedAccounts: {
            enabled: _get(_client, 'settings.login.blockCreatedAccounts.enabled', false),
          },
        },
        support: _get(_client, 'settings.support', defaultSupportFormValue),
        info: {
          timeZone: _get(_client, 'settings.info.timeZone', 'America/Detroit'), // legacy client timeZone
          logoUrl: _get(_client, 'settings.info.logoUrl'),
        },
        terminology: _get(_client, 'settings.terminology'),
        dottedLineRelationships: {
          enabled: _get(_client, ['settings', 'dottedLineRelationships', 'enabled']),
          userEditable: _get(_client, ['settings', 'dottedLineRelationships', 'userEditable']),
        },
      },
    },
    layouts: {
      'org-levels': _get(userConfig, '_layouts.org-levels', []),
    },
    theme: {
      mode: activeTheme ? 'custom' : 'default',
      logoUrl: '',
      backgroundUrl: '',
      pictureUrl: '',
      settings: {
        enableEmailTheming: false,
        palette: {
          primary: {
            main: '',
          },
          secondary: {
            main: '',
          },
          matched: {
            main: null,
          },
        },
      },
      ...activeTheme,
    },
  } as Types.GeneralValues;
  const status = useSelector((state: any) => state.client.status);
  const updateLayoutFetch = makeUpdateLayoutFetch();
  const userStatus = useStatus(updateLayoutFetch);
  const fields = _get(userConfig, '_fields');

  useEffect(() => {
    document.title = 'Settings';
  }, []);

  const history = useHistory();
  const tab = history.location.pathname.includes('theming') ? 'theming' : '';
  const description = {
    settings: 'Update your settings to personalize Sift for your organization.',
    theming: 'Customize the appearance of Sift to match your organization’s brand.',
  };

  useFormSnackbar({ status });

  const handleTabChange = (e: any, value: string) => {
    let pathname = '/admin/general';
    if (value) {
      pathname = `${pathname}/${value}`;
    }
    history.push({ pathname });
  };

  const handleSubmit = async (values: Types.GeneralValues) => {
    const _logoUrl = _get(values, 'client.settings.info.logoUrl');
    const _isLogoNew = _logoUrl !== initialState.client.settings.info.logoUrl;
    const logoToSave = _isLogoNew
      ? await fixImageUri(_client.clientId, 'sign-logo-upload', _logoUrl)
      : initialState.client.settings.info.logoUrl;

    const newClientValues = !_isEqual(initialState.client, values.client)
      ? {
          ...values.client,
          settings: {
            ...values.client.settings,
            info: {
              ...values.client.settings.info,
              logoUrl: logoToSave,
            },
          },
        }
      : null;

    const layoutPayloadKeys = Object.keys(values.layouts).filter(
      key => !_isEqual(values.layouts[key], initialState.layouts[key]),
    );
    const layoutPayload = layoutPayloadKeys.map(key => ({
      entityField: PERSON_ENTITY_TYPE_KEY,
      layoutKey: key,
      children: values.layouts[key],
    }));

    const newThemeValues = !_isEqual(initialState.theme, values.theme)
      ? {
          ...values.theme,
          isActive: true,
        }
      : null;
    newClientValues && (await dispatch(updateClient(newClientValues)));
    newThemeValues && dispatch(saveTheme(newThemeValues));
    layoutPayloadKeys.length && (await dispatch(updateLayoutFetch(layoutPayload)));
    dispatch(entityTypesFetch());

    if (_client.subdomain !== values.client.subdomain) {
      const hostName = getHostName();
      window.location.href = `https://${values.client.subdomain}.${hostName}.com/admin/general`;
    }
  };

  const handleValidate = (values: Types.GeneralValues) => {
    const errors = {} as Types.GeneralFormErrors;

    if (values.client.settings.support.enabled) {
      const supportEmails = values.client.settings.support.emails || [];
      const emailRegex = /^[A-Z0-9._%+-]+@(?:[A-Z0-9-]+\.)+[A-Z]{2,}$/i;

      supportEmails.forEach(email => {
        if (!emailRegex.test(email)) {
          errors.support = `${email} is not a valid email.`;
        }
      });

      if (supportEmails.length === 0) {
        errors.support = 'Add at least 1 support email address.';
      }
    }

    const teamMember = _get(values, ['client', 'settings', 'terminology', 'teamMember'], '');
    const teamLeader = _get(values, ['client', 'settings', 'terminology', 'teamLeader'], '');
    if (!teamMember.length) {
      errors.terminology = 'Team Member is a required field.';
    }

    if (!teamLeader.length) {
      errors.leaderTerminology = 'Team Leader is a required field.';
    }

    const primary = _get(values, 'theme.settings.palette.primary.main');
    const secondary = _get(values, 'theme.settings.palette.secondary.main');
    const matched = _get(values, 'theme.settings.palette.matched.main');
    if (
      !values.theme.settings &&
      !values.theme.logoUrl &&
      !values.theme.backgroundUrl &&
      !values.theme.pictureUrl
    ) {
      errors.theme = 'You must set at least one color or picture.';
    }

    if (primary && !isValidColor(primary)) {
      errors.theme = 'Invalid primary color.';
    }

    if (secondary && !isValidColor(secondary)) {
      errors.theme = 'Invalid secondary color.';
    }

    if (matched && !isValidColor(matched)) {
      errors.theme = 'Invalid match color.';
    }

    return errors;
  };

  return (
    <Page classes={{ root: classes.layoutRoot }}>
      <Breadcrumbs routes={['Home']} />
      <PageTitle
        title={tab === '' ? 'Settings' : 'Theming'}
        subtitle={tab === '' ? description.settings : description.theming}
      />
      <Loader status={status}>
        <Formik
          initialValues={initialState}
          // Pearl notes: `validate` and `validationSchema` can coexist,
          // their results get merged while `validate` takes priority.
          enableReinitialize={true}
          validationSchema={Yup.object({
            client: Yup.object().shape({
              displayName: Yup.string().required('Company Name is a required field.'),
            }),
          })}
          validate={handleValidate}
          onSubmit={handleSubmit}
        >
          {props => {
            const { values, setFieldValue, dirty, resetForm } = props;

            // (notes - pearl) Get subdomain validation error from API
            // validation dispatcher is in Subdomain
            const getErrors = () => {
              if (values.client.subdomain) {
                if (subdomainValidation && !subdomainValidation.isValid) {
                  return {
                    ...props.errors,
                    subdomain: _get(subdomainValidation, 'message'),
                  };
                }
              }

              return props.errors;
            };

            const errors = getErrors() as Types.GeneralFormErrors;

            return (
              <>
                <Form>
                  <Tabs onChange={(e, value) => handleTabChange(e, value)} value={tab || ''}>
                    <Tab label="General" value="" />
                    <Tab label="Theming" value="theming" />
                  </Tabs>
                  <div className={classes.root}>
                    {tab === '' && (
                      <SettingsPage
                        onChange={setFieldValue}
                        values={values}
                        errors={errors}
                        fields={fields}
                        userStatus={userStatus}
                      />
                    )}
                    {tab === 'theming' && <ThemingPage onChange={setFieldValue} values={values} />}
                  </div>
                  <PageFormToolbar
                    dirty={dirty}
                    preContent={<PageFormErrorMessage errors={errors} />}
                  >
                    <CancelButton onClick={resetForm} />
                    <SaveButton
                      onClick={async () => {
                        props.handleSubmit();
                        resetForm({ values });
                        await delay(2000);
                        history.location.pathname.includes('theming') &&
                          dispatch(getOnboardingTasks()) &&
                          analytics.track('Theme Saved', {
                            category: 'AD - General Settings',
                          });
                      }}
                      disabled={!dirty || !_isEmpty(errors)}
                    />
                  </PageFormToolbar>
                  <Prompt
                    when={!!dirty}
                    message="Your work is not saved! Are you sure you want to leave?"
                  />
                  <HandleRedirect />
                </Form>
              </>
            );
          }}
        </Formik>
      </Loader>
    </Page>
  );
}

const useStyles = makeStyles(theme => {
  return {
    root: {
      maxWidth: 1200,
    },
    layoutRoot: {
      display: 'flex',
      flex: '1 1 auto',
    },
  };
});

export default General;
